import React, { useCallback, useEffect, useMemo, useState } from "react";
import HoverPreview from "../../common/HoverPreview/HoverPreview";
import QmsDocumentPreviewPane from "./QmsDocumentPreviewPane";
import QmsVaultCommentsPanel from "./QmsVaultCommentsPanel";
import {
  deleteDocumentComment,
  editDocumentComment,
  fetchSharedWithMeDocumentById,
  saveDocumentComment,
  submitSharedWithMeApproval,
  submitSharedWithMeReview,
} from "./qmsService";
import {
  apiMessage,
  formatVaultApiError,
  getApiTable,
  isApiSuccess,
} from "./qmsApiUtils";
import {
  getSharedWithMeApprovalCompleteMessage,
  getSharedWithMeDisplayStage,
  getSharedWithMeLastApproval,
  getSharedWithMeLastReview,
  getSharedWithMeOwnerName,
  getSharedWithMeQueueLabel,
  getSharedWithMeReviewCompleteMessage,
  getSharedWithMeSentDate,
  isSharedWithMeApprovalActionComplete,
  isSharedWithMeApprovalRow,
  isSharedWithMeReviewActionComplete,
  isSharedWithMeReviewRow,
} from "./qmsSharedWithMeUtils";
import {
  buildStaffByIdMap,
  getVaultDocumentIdAnchorHtml,
  getVaultDocumentListId,
  getVaultDocumentTitle,
  getVaultStageClass,
  getVaultWorkspaceDocumentDetails,
} from "./qmsVaultUtils";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import {
  getVaultCommentMentionCandidates,
  patchVaultWorkspaceDocumentLog,
} from "./qmsVaultWorkspaceUtils";
import { fetchStaffLookup } from "../../../services/lookupService";

function DetailField({ label, value }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value || "—"}</dd>
    </div>
  );
}

function QmsSharedWithMeWorkspaceModal({
  open,
  row,
  userData,
  onClose,
  onNotify,
  onSubmitted,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [workspaceResponse, setWorkspaceResponse] = useState(null);
  const [error, setError] = useState("");
  const [comments, setComments] = useState("");
  const [reviewComplete, setReviewComplete] = useState(true);
  const [approvalStatus, setApprovalStatus] = useState("3");
  const [railView, setRailView] = useState("submit");
  const [staffRows, setStaffRows] = useState([]);

  const staffById = useMemo(() => buildStaffByIdMap(staffRows), [staffRows]);

  const workflow = useMemo(() => {
    if (!row) return null;
    if (isSharedWithMeReviewRow(row)) return "review";
    if (isSharedWithMeApprovalRow(row)) return "approval";
    return null;
  }, [row]);

  const documentInfo = useMemo(
    () => getApiTable(workspaceResponse, "DocumentInfo")[0] || row || {},
    [workspaceResponse, row],
  );

  const lastReview = useMemo(
    () => getSharedWithMeLastReview(workspaceResponse),
    [workspaceResponse],
  );
  const lastApproval = useMemo(
    () => getSharedWithMeLastApproval(workspaceResponse),
    [workspaceResponse],
  );

  const reviewActionLocked = useMemo(
    () => isSharedWithMeReviewActionComplete(lastReview),
    [lastReview],
  );

  const approvalActionLocked = useMemo(
    () => isSharedWithMeApprovalActionComplete(lastApproval),
    [lastApproval],
  );

  const headerStage = useMemo(() => {
    const lastAction = isSharedWithMeReviewRow(row) ? lastReview : lastApproval;
    const stage = getSharedWithMeDisplayStage(
      { ...row, DocumentStage: documentInfo.DocumentStage || row?.DocumentStage },
      lastAction,
    );
    return stage && stage !== "—" ? stage : "";
  }, [documentInfo.DocumentStage, lastApproval, lastReview, row]);

  const reload = useCallback(async () => {
    if (!row?.DocumentId || !userData?.StaffId) return null;
    setLoading(true);
    setError("");
    try {
      const response = await fetchSharedWithMeDocumentById(userData, row.DocumentId);
      if (!isApiSuccess(response)) {
        throw new Error(apiMessage(response) || "Could not load document.");
      }
      setWorkspaceResponse(response);
      return response;
    } catch (err) {
      setError(err?.message || "Could not load document.");
      setWorkspaceResponse(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [row?.DocumentId, userData]);

  useEffect(() => {
    if (!open || !userData?.StaffId) return undefined;
    let cancelled = false;
    fetchStaffLookup(userData)
      .then((staff) => {
        if (!cancelled) setStaffRows(Array.isArray(staff) ? staff : []);
      })
      .catch(() => {
        if (!cancelled) setStaffRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, userData]);

  useEffect(() => {
    if (!open || !row?.DocumentId) {
      setWorkspaceResponse(null);
      setError("");
      setComments("");
      setReviewComplete(true);
      setApprovalStatus("3");
      setRailView("submit");
      return;
    }
    reload();
  }, [open, row?.DocumentId, reload]);

  const notifySuccess = useCallback(
    (message) => {
      onNotify?.({ title: "Success", message, type: "success" });
    },
    [onNotify],
  );

  const notifyError = useCallback(
    (message) => {
      onNotify?.({ title: "Error", message, type: "danger" });
    },
    [onNotify],
  );

  const runCommentAction = useCallback(
    async (action, successMessage) => {
      setSaving(true);
      try {
        const response = await action();
        if (!isApiSuccess(response)) {
          throw new Error(formatVaultApiError(apiMessage(response)));
        }
        setWorkspaceResponse((prev) => patchVaultWorkspaceDocumentLog(prev || {}, response));
        notifySuccess(successMessage);
        return response;
      } catch (err) {
        notifyError(err?.message || "Request failed.");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [notifyError, notifySuccess],
  );

  const handleSubmitReview = async () => {
    if (reviewActionLocked) {
      onNotify?.({
        title: "Review complete",
        message: getSharedWithMeReviewCompleteMessage(),
        type: "info",
      });
      return;
    }
    if (!comments.trim()) {
      onNotify?.({
        title: "Review required",
        message: "Enter your review comments before submitting.",
        type: "warning",
      });
      return;
    }

    // Completing review requires auth modal (password confirmation).
    if (reviewComplete) {
      const authRes = await openAuthModal({
        showDescription: false,
        introMessage: "Confirm your identity to mark this review as complete.",
      });
      if (authRes?.Success !== 1) {
        if (!authRes?.cancelled) {
          notifyError("Authentication failed or was cancelled.");
        }
        return;
      }
    }

    setSaving(true);
    try {
      const response = await submitSharedWithMeReview(userData, {
        documentId: row.DocumentId,
        reviewerStatus: reviewComplete ? 2 : 1,
        comments: comments.trim(),
      });
      if (!isApiSuccess(response)) {
        throw new Error(apiMessage(response) || "Review submission failed.");
      }
      notifySuccess(apiMessage(response) || "Review submitted.");
      setComments("");
      await reload();
      onSubmitted?.();
      if (reviewComplete) {
        onClose?.();
      }
    } catch (err) {
      notifyError(err?.message || "Could not submit review.");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitApproval = async () => {
    if (approvalActionLocked) {
      onNotify?.({
        title: "Approval already submitted",
        message: getSharedWithMeApprovalCompleteMessage(lastApproval),
        type: "info",
      });
      return;
    }
    if (!comments.trim()) {
      onNotify?.({
        title: "Comment required",
        message: "Enter approval comments before submitting.",
        type: "warning",
      });
      return;
    }
    if (!["2", "3"].includes(String(approvalStatus))) {
      onNotify?.({
        title: "Status required",
        message: "Select Approved or Rejected.",
        type: "warning",
      });
      return;
    }

    // Approve / Reject requires auth modal (password confirmation).
    const authRes = await openAuthModal({
      showDescription: false,
      introMessage:
        String(approvalStatus) === "3"
          ? "Confirm your identity to approve this document."
          : "Confirm your identity to reject this document.",
    });
    if (authRes?.Success !== 1) {
      if (!authRes?.cancelled) {
        notifyError("Authentication failed or was cancelled.");
      }
      return;
    }

    setSaving(true);
    try {
      const response = await submitSharedWithMeApproval(userData, {
        documentId: row.DocumentId,
        approverStatus: Number(approvalStatus),
        comments: comments.trim(),
      });
      if (!isApiSuccess(response)) {
        throw new Error(apiMessage(response) || "Approval submission failed.");
      }
      notifySuccess(apiMessage(response) || "Approval submitted.");
      setComments("");
      await reload();
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      notifyError(err?.message || "Could not submit approval.");
    } finally {
      setSaving(false);
    }
  };

  const renderDetailsRail = () => {
    if (loading) {
      return <p className="text-muted mb-0 small">Loading document details…</p>;
    }
    if (error) {
      return <p className="text-danger mb-0 small">{error}</p>;
    }

    const details = getVaultWorkspaceDocumentDetails(workspaceResponse || {}, row);
    const info = documentInfo;
    const ownerName = getSharedWithMeOwnerName(info, staffById) || "—";
    const sentDate = getSharedWithMeSentDate(row);

    return (
      <dl className="qms-vault-workspace__details-grid">
        <div className="qms-vault-workspace__details-id">
          <dt>Document ID</dt>
          <dd>
            <HoverPreview
              isHtml
              content={details.documentIdHelpHtml}
              anchorHtml={getVaultDocumentIdAnchorHtml(info)}
              plainTextForPreview={details.documentId}
              previewLength={details.documentId.length + 1}
              appendEllipsis={false}
              maxWidth={280}
              maxHeight={220}
              className="qms-vault-workspace__details-id-hover"
            />
          </dd>
        </div>
        <DetailField label="Title" value={details.displayTitle} />
        <DetailField label="Owner" value={ownerName} />
        <DetailField label="Stage" value={details.stage} />
        <DetailField label="Queue" value={getSharedWithMeQueueLabel(row)} />
        <DetailField label="Sent" value={sentDate} />
        <DetailField label="Modified" value={details.modified} />
        <DetailField label="Version" value={details.version} />
        <DetailField label="Type" value={details.type} />
        <DetailField label="Category" value={details.category} />
      </dl>
    );
  };

  const renderCommentsRail = () => (
    <QmsVaultCommentsPanel
      active={railView === "comments"}
      variant="rail"
      documentId={row.DocumentId}
      userData={userData}
      staffOptions={getVaultCommentMentionCandidates(workspaceResponse || {})}
      saving={saving}
      onSave={({ comment, previousCommentId, assignedStaff, commentPriority }) =>
        runCommentAction(
          () =>
            saveDocumentComment(userData, {
              DocumentId: row.DocumentId,
              Comment: comment,
              PreviousCommentId: previousCommentId || "",
              AssignedStaff: assignedStaff || "",
              CommentPriority: commentPriority,
            }),
          "Comment posted.",
        )
      }
      onEdit={({ commentId, comment, assignedStaff, commentPriority, previousCommentId }) =>
        runCommentAction(
          () =>
            editDocumentComment(userData, {
              DocumentId: row.DocumentId,
              CommentId: commentId,
              Comment: comment,
              PreviousCommentId: previousCommentId || "",
              AssignedStaff: assignedStaff || "",
              CommentPriority: commentPriority,
            }),
          "Comment updated.",
        )
      }
      onDelete={({ commentId }) =>
        runCommentAction(
          () =>
            deleteDocumentComment(userData, {
              DocumentId: row.DocumentId,
              CommentId: commentId,
            }),
          "Comment deleted.",
        )
      }
    />
  );

  const renderSubmitRail = () => {
    if (workflow === "review") {
      if (reviewActionLocked) {
        return (
          <div className="qms-shared-with-me-modal__panel">
            <h6>Review complete</h6>
            <div className="alert alert-info py-2 px-3 small mb-3">
              {getSharedWithMeReviewCompleteMessage()}
            </div>
            {lastReview ? (
              <div className="qms-shared-with-me-modal__history">
                <strong>Your submission</strong>
                <div className="small text-muted mt-1">
                  {lastReview.ReviewedDateTime || "—"}
                </div>
                <div className="small mt-1">{lastReview.ReviewerComment || "—"}</div>
              </div>
            ) : null}
          </div>
        );
      }

      return (
        <div className="qms-shared-with-me-modal__panel">
          <h6>Submit review</h6>
          <p className="text-muted small">
            View the document on the left. Add comments in the Comments tab or submit your
            review here when ready.
          </p>
          <label className="form-label" htmlFor="shared-review-comments">
            Review comments
          </label>
          <textarea
            id="shared-review-comments"
            className="form-control form-control-sm"
            rows={5}
            value={comments}
            onChange={(event) => setComments(event.target.value)}
            placeholder="Enter your review…"
            disabled={saving}
          />
          <label className="qms-shared-with-me-modal__check mt-2">
            <input
              type="checkbox"
              checked={reviewComplete}
              onChange={(event) => setReviewComplete(event.target.checked)}
              disabled={saving}
            />
            <span>Review complete</span>
          </label>
          <p className="text-muted small mb-0 mt-1">
            If checked, you will not be able to review this document again until the owner
            assigns you again.
          </p>
          <button
            type="button"
            className="btn btn-primary btn-sm mt-3"
            disabled={saving}
            onClick={handleSubmitReview}
          >
            {saving ? "Submitting…" : "Submit review"}
          </button>
          {lastReview && !isSharedWithMeReviewActionComplete(lastReview) ? (
            <div className="qms-shared-with-me-modal__history mt-3">
              <strong>Last submission</strong>
              <div className="small text-muted mt-1">{lastReview.ReviewedDateTime || "—"}</div>
              <div className="small mt-1">{lastReview.ReviewerComment || "—"}</div>
            </div>
          ) : null}
        </div>
      );
    }

    if (workflow === "approval") {
      if (approvalActionLocked) {
        return (
          <div className="qms-shared-with-me-modal__panel">
            <h6>Approval submitted</h6>
            <div className="alert alert-info py-2 px-3 small mb-3">
              {getSharedWithMeApprovalCompleteMessage(lastApproval)}
            </div>
            {lastApproval ? (
              <div className="qms-shared-with-me-modal__history">
                <strong>Your submission</strong>
                <div className="small text-muted mt-1">
                  {lastApproval.ApprovedDateTime || "—"}
                </div>
                <div className="small mt-1">{lastApproval.ApproverComment || "—"}</div>
              </div>
            ) : null}
          </div>
        );
      }

      return (
        <div className="qms-shared-with-me-modal__panel">
          <h6>Submit approval</h6>
          <p className="text-muted small">
            View the document on the left. Add comments in the Comments tab or submit your
            approval decision here.
          </p>
          <label className="form-label" htmlFor="shared-approval-status">
            Decision
          </label>
          <select
            id="shared-approval-status"
            className="form-select form-select-sm"
            value={approvalStatus}
            onChange={(event) => setApprovalStatus(event.target.value)}
            disabled={saving}
          >
            <option value="3">Approved</option>
            <option value="2">Rejected</option>
          </select>
          <label className="form-label mt-2" htmlFor="shared-approval-comments">
            Comments
          </label>
          <textarea
            id="shared-approval-comments"
            className="form-control form-control-sm"
            rows={5}
            value={comments}
            onChange={(event) => setComments(event.target.value)}
            placeholder="Enter your approval comments…"
            disabled={saving}
          />
          <p className="text-muted small mb-0 mt-1">
            After you approve or reject, you will not be able to change this decision until
            the owner assigns you again.
          </p>
          <button
            type="button"
            className="btn btn-primary btn-sm mt-3"
            disabled={saving}
            onClick={handleSubmitApproval}
          >
            {saving ? "Submitting…" : "Submit approval"}
          </button>
          {lastApproval && !isSharedWithMeApprovalActionComplete(lastApproval) ? (
            <div className="qms-shared-with-me-modal__history mt-3">
              <strong>Last submission</strong>
              <div className="small text-muted mt-1">{lastApproval.ApprovedDateTime || "—"}</div>
              <div className="small mt-1">{lastApproval.ApproverComment || "—"}</div>
            </div>
          ) : null}
        </div>
      );
    }

    return <p className="text-muted mb-0 small">No review or approval action is available.</p>;
  };

  if (!open || !row) return null;

  const submitTabLabel = workflow === "approval" ? "Approval" : "Review";
  const ownerName = getSharedWithMeOwnerName(documentInfo, staffById);

  return (
    <>
      <div
        className="modal fade show d-block qms-doc-workspace-modal qms-vault-workspace-modal qms-shared-with-me-modal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-label="Shared document workspace"
      >
        <div className="modal-dialog qms-doc-workspace-modal__dialog">
          <div className="modal-content qms-doc-workspace-modal__content">
            <div className="qms-doc-workspace-modal__header">
              <div className="qms-doc-workspace-modal__header-main">
                <div className="qms-doc-workspace-modal__icon" aria-hidden="true">
                  <i className="fas fa-share-alt" />
                </div>
                <div>
                  <h5 className="qms-doc-workspace-modal__title">
                    {getVaultDocumentTitle(documentInfo)}
                  </h5>
                  <div className="qms-doc-workspace-modal__code-row">
                    <div className="qms-doc-workspace-modal__code">
                      {getVaultDocumentListId(documentInfo)}
                    </div>
                    {headerStage ? (
                      <span
                        className={`qms-doc-workspace-modal__stage ${getVaultStageClass(headerStage)}`}
                      >
                        {headerStage}
                      </span>
                    ) : null}
                    {ownerName ? (
                      <span className="qms-shared-with-me-modal__owner-meta">
                        Owner: {ownerName}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="qms-doc-workspace-modal__header-actions">
                <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
              </div>
            </div>

            <div className="qms-doc-workspace-modal__body qms-shared-with-me-modal__body">
              <div className="qms-doc-workspace-modal__main qms-vault-workspace-modal__split">
                <div className="qms-vault-workspace__panel-main is-preview">
                  <div className="qms-vault-workspace__panel-stack">
                    <div className="qms-vault-workspace__panel-layer is-active">
                      <div className="qms-doc-workspace__preview-pane">
                        {loading ? (
                          <div className="qms-shared-with-me-modal__loading">
                            <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                            <span>Loading document…</span>
                          </div>
                        ) : error ? (
                          <div className="p-3">
                            <div className="alert alert-danger mb-0">{error}</div>
                          </div>
                        ) : (
                          <div className="qms-vault-workspace__preview-body">
                            <QmsDocumentPreviewPane
                              active
                              documentId={
                                documentInfo.DocumentId ||
                                documentInfo.DocumentIdRef ||
                                row.DocumentId ||
                                row.DocumentIdRef ||
                                ""
                              }
                              documentUrl={documentInfo.DocumentURL || row.DocumentURL}
                              documentExtension={
                                documentInfo.DocumentExtension || row.DocumentExtension
                              }
                              documentTitle={getVaultDocumentTitle(documentInfo)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <aside
                  className={`qms-vault-workspace__details-rail${
                    railView === "comments" ? " is-comments-mode" : ""
                  }${railView === "submit" ? " is-submit-mode" : ""}`}
                  aria-label="Document actions"
                >
                  <div
                    className="qms-vault-workspace__rail-toggle is-three-tabs"
                    role="tablist"
                    aria-label="Right panel"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={railView === "details"}
                      className={`qms-vault-workspace__rail-toggle-btn${
                        railView === "details" ? " is-active" : ""
                      }`}
                      onClick={() => setRailView("details")}
                    >
                      Details
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={railView === "comments"}
                      className={`qms-vault-workspace__rail-toggle-btn${
                        railView === "comments" ? " is-active" : ""
                      }`}
                      onClick={() => setRailView("comments")}
                    >
                      Comments
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={railView === "submit"}
                      className={`qms-vault-workspace__rail-toggle-btn${
                        railView === "submit" ? " is-active" : ""
                      }`}
                      onClick={() => setRailView("submit")}
                    >
                      {submitTabLabel}
                    </button>
                  </div>

                  {railView === "details" ? (
                    <div className="qms-vault-workspace__rail-details-body">
                      {renderDetailsRail()}
                    </div>
                  ) : null}
                  {railView === "comments" ? (
                    <div className="qms-vault-workspace__rail-comments-body">
                      {renderCommentsRail()}
                    </div>
                  ) : null}
                  {railView === "submit" ? (
                    <div className="qms-vault-workspace__rail-details-body">
                      {renderSubmitRail()}
                    </div>
                  ) : null}
                </aside>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
}

export default QmsSharedWithMeWorkspaceModal;
